import { MARGIN_LEFT, MARGIN_RIGHT } from '../../utils/pdfConstants';
import { mittausCacheGet } from '../../../utils/mittausCache';
import { naytteetListGet } from '../../../utils/mittausCache';

// A4 landscape content width
const LANDSCAPE_W = Math.round(841.89 - MARGIN_LEFT - MARGIN_RIGHT); // ~785pt (unused but documents the budget)

const GREEN = '#04aa00';
const GREEN2 = '#038800';
const F = { font: 'Lato', fontSize: 7 };
const TH = { ...F, bold: true, color: '#fff', fillColor: GREEN, alignment: 'center', verticalAlignment: 'middle' };
const TH2 = { ...TH, fillColor: GREEN2 };
const TD = (opts = {}) => ({ ...F, alignment: 'center', verticalAlignment: 'middle', ...opts });
const E = { text: '', ...F, verticalAlignment: 'middle' };

function v(val) { return (val !== null && val !== undefined && val !== '') ? String(val) : '—'; }

function blockCaption(blockData, projekt) {
  const raw = blockData?.caption;
  if (!raw) return projekt?.nimi ?? '';
  return (typeof raw === 'string' ? raw : raw.value ?? '').trim() || (projekt?.nimi ?? '');
}

const tableLayout = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => '#adb5bd',
  vLineColor: () => '#adb5bd',
  paddingLeft:   () => 3,
  paddingRight:  () => 3,
  paddingTop:    () => 5,
  paddingBottom: () => 2,
  fillColor: (row) => row > 2 && row % 2 === 0 ? '#f8f9fa' : null,
};

const headerRows = [
  [
    { text: 'Näyte-\ntunnus',                   ...TH, rowSpan: 3 },
    { text: 'Rakenneosa',                        ...TH, rowSpan: 3 },
    { text: 'Testi',                             ...TH, rowSpan: 3 },
    { text: 'Testaus-\nsyvyys\npinta/mm',        ...TH, rowSpan: 3 },
    { text: 'Näytteen\nØ mm',                    ...TH, rowSpan: 3 },
    { text: 'Teräksen\nsijainti\nmm (Ø mm)',     ...TH, rowSpan: 3 },
    { text: 'Karbonatisoitumissyvyys mm',        ...TH, colSpan: 6 }, E, E, E, E, E,
    { text: 'Lisähuomioita',                     ...TH, rowSpan: 3 },
    { text: 'Rakenne-\npaksuus\nmm',             ...TH, rowSpan: 3 },
  ],
  [
    E, E, E, E, E, E,
    { text: 'up/yp/rp0', ...TH2, colSpan: 3 }, E, E,
    { text: 'sp/ap/rp1', ...TH2, colSpan: 3 }, E, E,
    E, E,
  ],
  [
    E, E, E, E, E, E,
    { text: 'min', ...TH2 }, { text: 'max', ...TH2 }, { text: 'k-m', ...TH2 },
    { text: 'min', ...TH2 }, { text: 'max', ...TH2 }, { text: 'k-m', ...TH2 },
    E, E,
  ],
];

const DATA_FIELDS = ['nayte_tunnus', 'rakenneosa', 'testi', 'testaussyvyys', 'naytteen_pinta',
  'terakseen_sijainti', 'lisahuomioita', 'rakennepaksuus'];

function hasData(nayte) {
  return DATA_FIELDS.some(f => nayte[f] !== '' && nayte[f] != null);
}

export function renderPoranayteBlock({ block, content, rakennusData, tableCounter }) {
  const blockData = block?.data ?? {};
  const projektId = blockData.mittausProjektiId;
  if (!projektId) return;

  const projekt = mittausCacheGet(projektId);
  const naytteet = naytteetListGet(projektId) ?? [];
  const rows = naytteet
    .filter(hasData)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  if (!rows.length) return;

  const tblNum = tableCounter ? ++tableCounter.value : null;

  const karbSettings = blockData.karbSettings ?? {};

  // Build tunnus → { upper_km, lower_km } from karb_per_nayte on each kohde
  const karbPerNayte = {};
  for (const kohde of (projekt?.kohteet ?? [])) {
    for (const [tunnus, vals] of Object.entries(kohde.data?.karb_per_nayte ?? {})) {
      karbPerNayte[tunnus] = { ...(karbPerNayte[tunnus] ?? {}), ...vals };
    }
  }

  const kv = (n, field) => {
    const fromSettings = karbSettings[n.id]?.[field];
    if (fromSettings != null) return fromSettings;
    if (n[field] != null) return n[field];
    if (field === 'karb_yp_km') return karbPerNayte[n.nayte_tunnus]?.upper_km ?? null;
    if (field === 'karb_ap_km') return karbPerNayte[n.nayte_tunnus]?.lower_km ?? null;
    return null;
  };

  const dataRows = rows.map(n => [
    TD({ text: v(n.nayte_tunnus) }),
    TD({ text: v(n.rakenneosa) }),
    TD({ text: v(n.testi) }),
    TD({ text: v(n.testaussyvyys) }),
    TD({ text: v(n.naytteen_pinta) }),
    TD({ text: v(n.terakseen_sijainti) }),
    TD({ text: v(kv(n, 'karb_yp_min')) }),
    TD({ text: v(kv(n, 'karb_yp_max')) }),
    TD({ text: v(kv(n, 'karb_yp_km')) }),
    TD({ text: v(kv(n, 'karb_ap_min')) }),
    TD({ text: v(kv(n, 'karb_ap_max')) }),
    TD({ text: v(kv(n, 'karb_ap_km')) }),
    TD({ text: v(n.lisahuomioita), alignment: 'left' }),
    TD({ text: v(n.rakennepaksuus) }),
  ]);

  const isLandscape = !!blockData.landscapePage;

  const tableNode = {
    table: {
      headerRows: 3,
      widths: isLandscape
        ? [30, 70, 34, 30, 24, 28, 18, 18, 20, 18, 18, 20, '*', 60]
        : [24, 50, 28, 28, 22, 26, 15, 15, 15, 15, 15, 15, '*', 56],
      body: [...headerRows, ...dataRows],
    },
    layout: tableLayout,
    margin: [0, 5, 0, 0],
  };

  const captionNode = tblNum !== null ? {
    text: [{ text: `Taulukko ${tblNum}. `, bold: true }, { text: blockCaption(blockData, projekt), bold: false }],
    font: 'Lato', fontSize: 9,
    margin: [0, 4, 0, 0],
  } : null;

  const stackItems = [tableNode, ...(captionNode ? [captionNode] : [])];

  if (isLandscape) {
    content.push({
      pageBreak: 'before',
      pageOrientation: 'landscape',
      stack: stackItems,
      margin: [0, 0, 0, 15],
    });
    // Switch back to portrait for content that follows
    content.push({ text: '', pageBreak: 'before', pageOrientation: 'portrait' });
  } else {
    content.push({
      stack: stackItems,
      margin: [0, 0, 0, 15],
    });
  }
}
